import BoltIcon from "@mui/icons-material/BoltRounded";
import CableIcon from "@mui/icons-material/CableRounded";
import ConstructionIcon from "@mui/icons-material/ConstructionRounded";
import GavelIcon from "@mui/icons-material/GavelOutlined";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import MemoryIcon from "@mui/icons-material/MemoryRounded";
import PowerIcon from "@mui/icons-material/PowerSettingsNewRounded";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import RequestQuoteIcon from "@mui/icons-material/RequestQuoteOutlined";
import RestoreIcon from "@mui/icons-material/RestoreRounded";
import SensorsIcon from "@mui/icons-material/SensorsRounded";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAltRounded";
import ThermostatIcon from "@mui/icons-material/ThermostatRounded";
import type { ElementType } from "react";

import type { CategoryKey, RigorTier } from "./types";

/**
 * Static catalogue of the 14 embedded-systems categories. Mirrored
 * (label / prefix / essentials) from `backend/requirements/schemas.py`
 * `CATEGORY_PREFIX` and the catalogue in
 * `backend/requirements/mockData.ts` (now retired). Keep these in
 * lockstep with the backend.
 *
 * `essentials` is the per-tier count of "expected" requirements the
 * UI uses to render the completeness count. It's a UX nudge, not an
 * enforcement gate — the backend's tier-upgrade validator is what
 * actually decides whether a tier flip is allowed.
 */

export interface CategoryDescriptor {
  key: CategoryKey;
  prefix: string;
  label: string;
  blurb: string;
  icon: ElementType;
  essentials: Record<RigorTier, number>;
}

export const CATEGORIES: CategoryDescriptor[] = [
  {
    key: "functional",
    prefix: "FN",
    label: "Functional",
    blurb: "What the device does — sense, process, output.",
    icon: BoltIcon,
    essentials: { concept: 3, product: 5, regulated: 7 },
  },
  {
    key: "power",
    prefix: "PWR",
    label: "Power",
    blurb: "Source, budget, sleep modes, runtime, charging.",
    icon: PowerIcon,
    essentials: { concept: 2, product: 5, regulated: 6 },
  },
  {
    key: "compute",
    prefix: "CPU",
    label: "Compute & Memory",
    blurb: "MCU class, RAM, flash, OS choice.",
    icon: MemoryIcon,
    essentials: { concept: 1, product: 4, regulated: 5 },
  },
  {
    key: "interfaces",
    prefix: "IF",
    label: "Interfaces",
    blurb: "Wired and wireless I/O — protocols, connectors.",
    icon: CableIcon,
    essentials: { concept: 2, product: 5, regulated: 6 },
  },
  {
    key: "sensing",
    prefix: "SNS",
    label: "Sensing & Actuation",
    blurb: "Modalities, ranges, accuracy, sample rate.",
    icon: SensorsIcon,
    essentials: { concept: 1, product: 4, regulated: 5 },
  },
  {
    key: "mechanical",
    prefix: "MEC",
    label: "Mechanical & Enclosure",
    blurb: "Form factor, mounting, IP rating, drop spec.",
    icon: ConstructionIcon,
    essentials: { concept: 1, product: 3, regulated: 4 },
  },
  {
    key: "environmental",
    prefix: "ENV",
    label: "Environmental",
    blurb: "Operating temperature, humidity, vibration, shock.",
    icon: ThermostatIcon,
    essentials: { concept: 1, product: 4, regulated: 5 },
  },
  {
    key: "firmware",
    prefix: "FW",
    label: "Firmware & Update",
    blurb: "Boot, OTA, rollback, secure boot.",
    icon: SystemUpdateAltIcon,
    essentials: { concept: 1, product: 3, regulated: 5 },
  },
  {
    key: "security",
    prefix: "SEC",
    label: "Security",
    blurb: "Key storage, encrypted comms, debug-port lockdown.",
    icon: ShieldIcon,
    essentials: { concept: 0, product: 3, regulated: 5 },
  },
  {
    key: "safety",
    prefix: "SAF",
    label: "Safety",
    blurb: "Fail-safe states, watchdog, fault detection.",
    icon: HealthAndSafetyIcon,
    essentials: { concept: 0, product: 2, regulated: 5 },
  },
  {
    key: "reliability",
    prefix: "REL",
    label: "Reliability & Lifecycle",
    blurb: "MTBF, design life, EOL parts policy.",
    icon: RestoreIcon,
    essentials: { concept: 0, product: 2, regulated: 4 },
  },
  {
    key: "compliance",
    prefix: "CMP",
    label: "Compliance",
    blurb: "FCC, CE, UL, RoHS — and framework-specific.",
    icon: GavelIcon,
    essentials: { concept: 0, product: 3, regulated: 6 },
  },
  {
    key: "manufacturing",
    prefix: "MFG",
    label: "Manufacturing & Test",
    blurb: "Volume, DFM/DFT, calibration, ICT/JTAG.",
    icon: PrecisionManufacturingIcon,
    essentials: { concept: 0, product: 2, regulated: 3 },
  },
  {
    key: "cost",
    prefix: "COST",
    label: "Cost",
    blurb: "BOM target, NRE, COGS at volume.",
    icon: RequestQuoteIcon,
    essentials: { concept: 1, product: 2, regulated: 2 },
  },
];

export const CATEGORY_BY_KEY: Record<CategoryKey, CategoryDescriptor> =
  Object.fromEntries(CATEGORIES.map((c) => [c.key, c])) as Record<
    CategoryKey,
    CategoryDescriptor
  >;
